import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { conditionalSocket } from "../../types";

/**
 * Decision operators for structured condition evaluation.
 */
export const DecisionOperatorSchema = z.enum([
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
  "greater_than_or_equal",
  "less_than_or_equal",
  "contains",
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
  "in",
]);

export type DecisionOperator = z.infer<typeof DecisionOperatorSchema>;

/**
 * Decision condition configuration.
 */
export const DecisionConditionSchema = z.object({
  operator: DecisionOperatorSchema,
  value: z
    .any()
    .optional()
    .describe("Comparison value (not needed for is_empty, is_true, etc.)"),
});

export type DecisionCondition = z.infer<typeof DecisionConditionSchema>;

/**
 * Decision Step
 *
 * General-purpose conditional branching. Evaluates a structured condition
 * against an input value and routes to one of two output branches.
 *
 * Example workflow:
 * ```
 * RandomNumber → Decision(operator: less_than, value: 5)
 *                  ├─ [matched] → HelloWorld("Low")
 *                  └─ [unmatched] → HelloWorld("High")
 * ```
 *
 * Skip propagation (in the orchestration engine) marks the untaken branch
 * as skipped so the workflow completes correctly.
 */
export const decisionStep = defineStepConfig({
  type: "decision",
  name: "Decision",
  description: "Conditional branching based on structured conditions",
  category: "Control Flow",

  inputSchema: z.object({
    value: z.any().describe("The value to evaluate against the condition"),
  }),

  outputSchema: z.object({
    matched: conditionalSocket(z.any())
      .optional()
      .describe("Input value when condition is met"),
    unmatched: conditionalSocket(z.any())
      .optional()
      .describe("Input value when condition is not met"),
  }),

  configSchema: z.object({
    condition: DecisionConditionSchema.default({ operator: "equals" }).describe(
      "The condition to evaluate",
    ),
  }),

  emits: [] as const,
});
